import { useState } from 'react';
import { toast } from 'sonner';
import { formatDistanceToNow } from 'date-fns';
import { Download, FileDown } from 'lucide-react';
import { Button } from '@/components/ui/button';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { DataTable, type DataTableColumn } from '@/components/DataTable';
import { StatusBadge, type StatusVariant } from '@/components/StatusBadge';
import { EmptyState } from '@/components/EmptyState';
import { SectionLabel } from '@/components/SectionLabel';
import { ApiError } from '@/lib/api/client';
import { useExportDownload, useWellExports } from '../api';
import type { ExportJobStatus, ExportJobView } from '../types';
import { ExportDialog } from './ExportDialog';

const STATUS_META: Record<ExportJobStatus, { variant: StatusVariant; label: string }> = {
  pending: { variant: 'neutral', label: 'Pending' },
  running: { variant: 'info', label: 'Running' },
  complete: { variant: 'success', label: 'Complete' },
  failed: { variant: 'danger', label: 'Failed' },
};

function fmtRange(job: ExportJobView): string {
  const d = (iso: string) => new Date(iso).toISOString().slice(0, 10);
  return `${d(job.from)} → ${d(job.to)}`;
}

export function ExportSection({ wells }: { wells: Array<{ id: string; name: string }> }) {
  const [selectedWellId, setSelectedWellId] = useState(wells[0]?.id ?? '');
  const [dialogOpen, setDialogOpen] = useState(false);

  const exportsQuery = useWellExports(selectedWellId || null);
  const download = useExportDownload();

  const onDownload = async (jobId: string) => {
    try {
      const view = await download.mutateAsync(jobId);
      if (view.downloadUrl) {
        window.open(view.downloadUrl, '_blank');
      } else {
        toast.error('Download link unavailable — try again.');
      }
    } catch (err) {
      toast.error(err instanceof ApiError ? err.message : 'Download failed');
    }
  };

  const columns: DataTableColumn<ExportJobView>[] = [
    { id: 'range', header: 'Range', cell: (j) => <span className="text-sm">{fmtRange(j)}</span> },
    {
      id: 'status',
      header: 'Status',
      cell: (j) => <StatusBadge {...STATUS_META[j.status]} />,
    },
    {
      id: 'created',
      header: 'Requested',
      cell: (j) => (
        <span className="text-xs text-muted-foreground">
          {formatDistanceToNow(new Date(j.createdAt), { addSuffix: true })}
        </span>
      ),
    },
    {
      id: 'action',
      header: '',
      className: 'text-right',
      cell: (j) =>
        j.status === 'complete' ? (
          <Button
            size="sm"
            variant="ghost"
            data-testid={`export-download-${j.id}`}
            onClick={() => void onDownload(j.id)}
          >
            <Download className="mr-1 h-3.5 w-3.5" /> Download
          </Button>
        ) : null,
    },
  ];

  return (
    <div className="space-y-3" data-testid="export-section">
      <div className="flex items-center justify-between">
        <SectionLabel>Exports</SectionLabel>
        <div className="flex items-center gap-2">
          <div className="w-44">
            <Select value={selectedWellId} onValueChange={setSelectedWellId}>
              <SelectTrigger data-testid="export-section-well" className="h-8">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {wells.map((w) => (
                  <SelectItem key={w.id} value={w.id}>
                    {w.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <Button size="sm" data-testid="export-open" onClick={() => setDialogOpen(true)}>
            <FileDown className="mr-1 h-3.5 w-3.5" /> Export CSV
          </Button>
        </div>
      </div>

      <DataTable
        columns={columns}
        rows={exportsQuery.data?.jobs}
        getRowKey={(j) => j.id}
        isLoading={exportsQuery.isLoading}
        isError={exportsQuery.isError}
        emptyState={
          <EmptyState title="No exports yet" description="Export a well's readings to CSV." />
        }
      />

      <ExportDialog
        open={dialogOpen}
        onOpenChange={setDialogOpen}
        wells={wells}
        defaultWellId={selectedWellId}
      />
    </div>
  );
}
